/**
 * Smart Action request origin: which Smart Action, step and screen a write
 * came from.
 *
 * The webapp sends these headers on writes made from a Smart Action screen and
 * the platform reads them to attribute the write. They are hints, not proof:
 * the platform drops malformed values, and they never authorize anything.
 */

import { isNanoid } from "./nanoid";

/** The Smart Action the write came from (a nanoid). Attribution needs it. */
export const SMART_ACTION_ID_HEADER = "X-Smart-Action-Id";

/** The step of that Smart Action, when the write came from one (a nanoid). */
export const SMART_ACTION_STEP_ID_HEADER = "X-Smart-Action-Step-Id";

/** The screen the write came from, one of `SMART_ACTION_SURFACES`. */
export const SMART_ACTION_SURFACE_HEADER = "X-Smart-Action-Surface";

/** Where a person acts on a Smart Action: the board, the action popup, the order card, the chat composer. */
export const SMART_ACTION_SURFACES = [
  "board",
  "popup",
  "order_card",
  "composer",
] as const;
export type SmartActionSurface = (typeof SMART_ACTION_SURFACES)[number];

/**
 * The Smart Action a request came from. Named apart from `SmartActionOrigin`
 * (`ai` / `manual`, who created an action).
 */
export interface SmartActionRequestOrigin {
  smartActionId: string;
  stepId: string | null;
  surface: SmartActionSurface | null;
}

/** The headers that carry `origin`, leaving out the step and surface when they have no value. */
export function smartActionOriginHeaders(
  origin: SmartActionRequestOrigin,
): Record<string, string> {
  return {
    [SMART_ACTION_ID_HEADER]: origin.smartActionId,
    ...(origin.stepId ? { [SMART_ACTION_STEP_ID_HEADER]: origin.stepId } : {}),
    ...(origin.surface
      ? { [SMART_ACTION_SURFACE_HEADER]: origin.surface }
      : {}),
  };
}

/**
 * Reads a request's Smart Action origin through `getHeader`, e.g.
 * `(name) => request.headers.get(name)`. `null` when the action id is missing
 * or not a nanoid; a malformed step id or an unknown surface becomes `null`
 * and the rest is kept.
 */
export function readSmartActionOrigin(
  getHeader: (name: string) => string | null,
): SmartActionRequestOrigin | null {
  const smartActionId = getHeader(SMART_ACTION_ID_HEADER);
  if (smartActionId === null || !isNanoid(smartActionId)) return null;

  const stepId = getHeader(SMART_ACTION_STEP_ID_HEADER);
  const surface = getHeader(SMART_ACTION_SURFACE_HEADER);
  return {
    smartActionId,
    stepId: stepId !== null && isNanoid(stepId) ? stepId : null,
    surface: isSmartActionSurface(surface) ? surface : null,
  };
}

function isSmartActionSurface(
  value: string | null,
): value is SmartActionSurface {
  return SMART_ACTION_SURFACES.some((surface) => surface === value);
}
